import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import axios from "../axios";
import toast from "../hooks/toast";
import { buildApiV3Url, buildApiV3UrlWithQuery } from "../shared/Utils";
import { CRUDOperation } from "../shared/constants";
import { Identifier } from "../shared/responseConstants";
import {
    cannotDeleteRecordResponse,
    errorResponse,
    parseGetAllObjResponse,
    parseResponseMessage,
    parseSuccessResponse,
} from "../shared/responseUtils";
import { setCertificateAttachmentLabels } from "./certificateSlice";
import type { RootState } from "./types";

interface PaginateState {
    pageNumber: number;
    startIndex: number;
    endIndex: number;
    rowsPerPage: number;
    totalRecords: number;
}

interface CertificateAttachmentLabel {
    id: number;
    name: string;
    description: string;
    systemCode?: boolean;
}

interface PageState {
    data: CertificateAttachmentLabel[];
    paginate: PaginateState;
}

interface FormState {
    name: string;
}

interface PaginateDetails {
    pageNumber?: number;
    startIndex?: number;
    endIndex?: number;
    rowsPerPage?: number;
}

const initialState = {
    isLoading: false,
    isDeleting: false,
    isAddOrEditLoading: false,
    enableLoading: false,
    page: {
        data: [] as CertificateAttachmentLabel[],
        paginate: {
            pageNumber: 0,
            startIndex: -1,
            endIndex: 0,
            rowsPerPage: 0,
            totalRecords: 0,
        },
    },
    sortColumn: ["name", true] as [string, boolean],
    formState: {
        name: "",
    },
    cannotDeleteRecordMessage: "",
};

export const certificateAttachmentLabelSlice = createSlice({
    name: "certificateAttachmentLabel",
    initialState,
    reducers: {
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setPage: (state, action: PayloadAction<PageState>) => {
            state.page = action.payload;
        },
        setPageToNull: state => {
            state.page = {
                data: [],
                paginate: {
                    pageNumber: 0,
                    startIndex: -1,
                    endIndex: 0,
                    rowsPerPage: 0,
                    totalRecords: 0,
                },
            };
        },
        setIsDeleting: (state, action: PayloadAction<boolean>) => {
            state.isDeleting = action.payload;
        },
        setIsAddOrEditLoading: (state, action: PayloadAction<boolean>) => {
            state.isAddOrEditLoading = action.payload;
        },
        setEnableLoading: (state, action: PayloadAction<boolean>) => {
            state.enableLoading = action.payload;
        },
        setSortColumn: (state, action: PayloadAction<[string, boolean]>) => {
            state.sortColumn = action.payload;
        },
        setFormState: (state, action: PayloadAction<FormState>) => {
            state.formState = action.payload;
        },
        setCannotDeleteRecordMessage: (state, action: PayloadAction<string>) => {
            state.cannotDeleteRecordMessage = action.payload;
        },
        clearFormState: state => {
            state.formState = initialState.formState;
        },
    },
});

type ShowToastFn = (status: string, text: string, id: string, persistent?: boolean) => void;
const [showToast] = toast() as [ShowToastFn];
export const {
    setIsLoading,
    setPage,
    setPageToNull,
    setFormState,
    clearFormState,
    setIsDeleting,
    setIsAddOrEditLoading,
    setEnableLoading,
    setSortColumn,
    setCannotDeleteRecordMessage,
} = certificateAttachmentLabelSlice.actions;

export const selectIsLoading = (state: RootState): boolean =>
    state.certificateAttachmentLabel.isLoading;
export const selectPage = (state: RootState): PageState => state.certificateAttachmentLabel.page;
export const selectFormState = (state: RootState): FormState =>
    state.certificateAttachmentLabel.formState;
export const selectIsDeleting = (state: RootState): boolean =>
    state.certificateAttachmentLabel.isDeleting;
export const selectIsAddOrEditLoading = (state: RootState): boolean =>
    state.certificateAttachmentLabel.isAddOrEditLoading;
export const selectEnableLoading = (state: RootState): boolean | undefined =>
    state.certificateAttachmentLabel.enableLoading;
export const selectSortColumn = (state: RootState): [string, boolean] =>
    state.certificateAttachmentLabel.sortColumn;
export const selectCannotDeleteRecordMessage = (state: RootState): string =>
    state.certificateAttachmentLabel.cannotDeleteRecordMessage;

const getApiBaseUrl = (getState: () => RootState): string | null => {
    const companyId = getState().session?.activeCompany?.id;
    if (!companyId) return null;
    return `companies/${companyId}/certificate-attachment-labels`;
};

export const fetchCertificateAttachmentLabels =
    (top?: number, skip?: number, paginateDetails?: PaginateDetails) =>
    async (dispatch: (action: unknown) => void, getState: () => RootState): Promise<void> => {
        const { isLoading, sortColumn, formState } = getState().certificateAttachmentLabel;
        const { rowsPerPage } = getState().session;
        const apiBase = getApiBaseUrl(getState);
        if (!apiBase) return;

        const effectiveTop = top != null && top > 0 ? top : rowsPerPage;
        const { name } = formState;
        if (isLoading) {
            return;
        }
        let filterString = "";
        dispatch(setIsLoading(true));
        const sort = `${sortColumn[0]} ${sortColumn[1] ? "ASC" : "DESC"}`;
        if (name) {
            filterString = `name contains '${name}' `;
        }

        try {
            const apiURI = buildApiV3UrlWithQuery(
                apiBase,
                filterString,
                null,
                effectiveTop,
                skip ?? 0,
                sort
            );
            const response = await axios.get(`${apiURI}`, { withCredentials: true });
            if (parseSuccessResponse(CRUDOperation.ReadAll, response)?.recordFound) {
                const readAllResponse = parseGetAllObjResponse(response);
                const paginateData = paginateDetails
                    ? {
                          pageNumber: paginateDetails?.pageNumber ?? 1,
                          startIndex: paginateDetails?.startIndex ?? 0,
                          endIndex: paginateDetails?.endIndex ?? 19,
                          rowsPerPage: paginateDetails?.rowsPerPage ?? 20,
                          totalRecords: readAllResponse?.count ?? 0,
                      }
                    : {
                          pageNumber: 1,
                          startIndex: 0,
                          endIndex: 19,
                          rowsPerPage: 20,
                          totalRecords: readAllResponse?.count ?? 0,
                      };
                dispatch(
                    setPage({
                        data: readAllResponse?.value ?? [],
                        paginate: paginateData,
                    })
                );
            } else {
                dispatch(setPageToNull());
            }
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const deleteCertificateAttachmentLabel =
    (id: number) =>
    async (dispatch: (action: unknown) => void, getState: () => RootState): Promise<boolean> => {
        const { isDeleting } = getState().certificateAttachmentLabel;
        const apiBase = getApiBaseUrl(getState);
        if (!apiBase) return false;

        let successResponse = true;

        if (isDeleting) {
            return false;
        }
        dispatch(setIsDeleting(true));
        const response = await axios
            .delete(buildApiV3Url(`${apiBase}/${id}`), {
                withCredentials: true,
            })
            .catch((error: Error) => error);
        const err = errorResponse(response);
        const errCode = typeof err?.code === "string" ? err.code.toLowerCase() : "";
        const isCannotDelete =
            errCode === Identifier.CannotDeleteRecord || cannotDeleteRecordResponse(response);
        if (isCannotDelete) {
            const rawMessage = (err?.message ?? parseResponseMessage(response)) as unknown;
            const apiMessage =
                typeof rawMessage === "string" ? rawMessage : "Unable to delete this label.";
            dispatch(setCannotDeleteRecordMessage(apiMessage));
            showToast("error", apiMessage, "deleteCertificateAttachmentLabelError", false);
            successResponse = false;
        } else if (parseSuccessResponse(CRUDOperation.Delete, response)?.isSuccess) {
            showToast("success", "Label deleted.", "deleteCertificateAttachmentLabelToast", false);
            dispatch(fetchCertificateAttachmentLabels());
            dispatch(setCannotDeleteRecordMessage(""));
            dispatch(setCertificateAttachmentLabels([]));
        }
        dispatch(setIsDeleting(false));
        return successResponse;
    };

interface AddOrEditPayload {
    name: string;
    description: string;
    systemCode: boolean;
}

export const addOrUpdateCertificateAttachmentLabels =
    (addOrEdit: "Add" | "Edit", payload: AddOrEditPayload, certificateLabelId?: number) =>
    async (dispatch: (action: unknown) => void, getState: () => RootState): Promise<boolean> => {
        const apiBase = getApiBaseUrl(getState);
        if (!apiBase) return false;

        dispatch(setIsAddOrEditLoading(true));
        let success = false;
        try {
            if (addOrEdit === "Add") {
                const response = await axios.post(buildApiV3Url(apiBase), payload, {
                    withCredentials: true,
                });
                if (parseSuccessResponse(CRUDOperation.Create, response)?.isSuccess) {
                    showToast("success", "Label added.", "label-success-toast", false);
                    success = true;
                    dispatch(fetchCertificateAttachmentLabels());
                    dispatch(setCertificateAttachmentLabels([]));
                }
            } else {
                const response = await axios.put(
                    buildApiV3Url(`${apiBase}/${certificateLabelId}`),
                    payload,
                    { withCredentials: true }
                );
                if (parseSuccessResponse(CRUDOperation.Update, response)?.isSuccess) {
                    showToast(
                        "success",
                        "Successfully updated certificate label",
                        "certificate-label-success-toast",
                        false
                    );
                    success = true;
                    dispatch(fetchCertificateAttachmentLabels());
                    dispatch(setCertificateAttachmentLabels([]));
                }
            }
        } catch (err) {
            const apiError = errorResponse(err);
            const message =
                typeof apiError?.message === "string" && apiError.message
                    ? apiError.message
                    : "Failed to save the label. Please try again.";
            showToast("error", message, "attachment-label-save-error", false);
        }
        dispatch(setIsAddOrEditLoading(false));
        return success;
    };

export const putCertificateAttachmentLabels =
    (payload: AddOrEditPayload, certificateLabelId: number) =>
    async (dispatch: (action: unknown) => void, getState: () => RootState): Promise<boolean> => {
        const apiBase = getApiBaseUrl(getState);
        if (!apiBase) return false;

        dispatch(setIsAddOrEditLoading(true));
        let success = false;
        try {
            const response = await axios.put(
                buildApiV3Url(`${apiBase}/${certificateLabelId}`),
                payload,
                { withCredentials: true }
            );
            if (parseSuccessResponse(CRUDOperation.Update, response)?.isSuccess) {
                showToast(
                    "success",
                    "Successfully updated the label",
                    "certificate-label-success-toast",
                    false
                );
                success = true;
                dispatch(fetchCertificateAttachmentLabels());
                dispatch(setCertificateAttachmentLabels([]));
            }
        } catch (err) {
            const apiError = errorResponse(err);
            const message =
                typeof apiError?.message === "string" && apiError.message
                    ? apiError.message
                    : "Failed to save the label. Please try again.";
            showToast("error", message, "attachment-label-save-error", false);
        }
        dispatch(setIsAddOrEditLoading(false));
        return success;
    };

export default certificateAttachmentLabelSlice.reducer;
